import { TradReelSet } from '@1x2network/reel_set'
import { AnyBaseGameModel } from '../model/BaseGameModel'
import { Anticipation } from './Anticipation'
import { SoundPlayer } from './SoundPlayer'
import { valueIds } from '../constants/valueIds'
import { CustomSymbol } from '../CustomSymbol'
import gsap from 'gsap'
import { createNudgeEffect, EffectManager } from '@1x2network/tweens'
import { slamStopHandler } from '@1x2network/duck_slot_game'

/**
 * Manager for components interacting with the game anticipation
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */
export class AnticipationManager {
    private reelset: TradReelSet<CustomSymbol>
    private anticipation: Anticipation
    private soundPlayer: SoundPlayer
    private effectManager: EffectManager

    constructor(
        reelset: TradReelSet<CustomSymbol>,
        anticipation: Anticipation,
        soundPlayer: SoundPlayer,
        effectManager: EffectManager,
    ) {
        this.reelset = reelset
        this.anticipation = anticipation
        this.soundPlayer = soundPlayer
        this.effectManager = effectManager
    }

    public initConnections(model: AnyBaseGameModel) {
        const { reelset, anticipation, soundPlayer, effectManager } = this
        const spinAnimator = reelset.spinAnimator
        const anticipate = this.calculateAnticipation(model)

        const anticipateConnection = spinAnimator.signals.onLand.connect((index) => {
            const isTransitioning = model.state.phase.next === 'HOLD_AND_WIN'
            const isAnticipating = anticipation.isActive
            const didValueSymbolLand = model.results.board.reels[index].some((s) => valueIds.includes(s.id))
            const nextReelShouldAnticipate = anticipate[index + 1] && anticipate[index + 1] > anticipate[index]

            // Nudge the reels when a symbol lands during anticipation
            if (isAnticipating && didValueSymbolLand) {
                effectManager.addAnimation(reelset, createNudgeEffect({ targetOffset: { y: 4 }, duration: 0.2 }))
            }

            // Final reel has landed
            if (isAnticipating && !nextReelShouldAnticipate) {
                anticipation.stop()
                soundPlayer.stopAnticipation()
                if (!isTransitioning) {
                    soundPlayer.playAnticipationMiss()
                }

                for (let i = 0; i <= index; i++) {
                    const landedSymbols = reelset.fetcher.landedSymbols[i]
                    landedSymbols
                        .filter((symbol) => !valueIds.includes(symbol.id))
                        .forEach((symbol) => {
                            gsap.to(symbol, { pixi: { tint: 0xffffff }, ease: 'none', duration: 0.2, overwrite: true })
                        })
                }

                return
            }

            // Next reel is anticipating
            if (nextReelShouldAnticipate) {
                soundPlayer.playAnticipation()
                anticipation.anticipateReel(index + 1)
                reelset.setSpinSpeed(index + 1, 1.5, { duration: 0.25 })

                for (let i = 0; i <= index; i++) {
                    const landedSymbols = reelset.fetcher.landedSymbols[i]
                    const valueSymbols = landedSymbols.filter((symbol) => valueIds.includes(symbol.id))
                    anticipation.anticipateSymbols(valueSymbols)
                    landedSymbols
                        .filter((symbol) => !valueIds.includes(symbol.id))
                        .forEach((symbol) => {
                            gsap.to(symbol, { pixi: { tint: 0x7f7f7f }, ease: 'none', duration: 0.2, overwrite: true })
                        })
                }
            }
        })

        const cleanup = () => {
            soundPlayer.stopAnticipation()
            cleanupConnection.disconnect()
            anticipateConnection.disconnect()
            slamStopConnection.disconnect()
        }

        const slamStopConnection = slamStopHandler.onSlamStop.connect(() => {
            cleanup()
            if (anticipation.isActive) {
                anticipation.stop()
            }

            for (let i = 0; i < 5; i++) {
                const landedSymbols = reelset.fetcher.landedSymbols[i]
                landedSymbols
                    .filter((symbol) => !valueIds.includes(symbol.id))
                    .forEach((symbol) => {
                        gsap.set(symbol, { pixi: { tint: 0xffffff }, ease: 'none', overwrite: true })
                    })
            }
        })

        const cleanupConnection = spinAnimator.signals.onAllComplete.connect(cleanup)
    }

    public calculateCoinStagger(model: AnyBaseGameModel): number[] {
        const reels = model.results.board.reels
        const output = [0, 0, 0, 0, 0]

        let c = 0
        for (let i = 0; i < reels.length; i++) {
            const reel = reels[i]
            const hasValueSymbol = reel.some((symbol) => valueIds.includes(symbol.id))

            output[i] = c * 3
            if (hasValueSymbol) {
                c++
            }
        }

        return output
    }

    public calculateAnticipation(model: AnyBaseGameModel) {
        const anticipate = new Set(model.results.anticipate?.anticipate)
        const isTransforming = model.results.wins.events.some((event) => event.type === 'TRANSFORM')
        const output = [0, 0, 0, 0, 0]
        if (!anticipate.size) {
            return output
        }

        if (!anticipate) {
            return output
        }

        // Not sure if backend cares about edge cases, but just in case don't anticipate during coin shower
        if (isTransforming) {
            return output
        }

        for (let i = 0; i < 5; i++) {
            const previousReelAnticipate = output[i - 1] || 0
            output[i] = anticipate.has(i) ? previousReelAnticipate + 8 : previousReelAnticipate
        }

        return output
    }
}
